/* ---------------------------------------------------------------
   Skills match — one source of truth for "what does this posting ask
   for, and how much of it do you already have".

   Deliberately deterministic and local: it runs on every card as jobs
   stream in, so it can't cost an API call. The AI "resume match" score
   in the analysis sheet is a different, deeper number — keep the two
   labelled differently in the UI so they never read as contradictory.
   --------------------------------------------------------------- */

/* Terms we can recognise in a posting. Aliases collapse the spellings a
   job description might use into one canonical skill. */
const SKILL_ALIASES = {
  React: ["react", "react.js", "reactjs"],
  Angular: ["angular", "angular.js", "angularjs"],
  Vue: ["vue", "vue.js", "vuejs"],
  "Next.js": ["next.js", "nextjs"],
  TypeScript: ["typescript"],
  JavaScript: ["javascript", "es6"],
  "Node.js": ["node.js", "nodejs"],
  Python: ["python"],
  Java: ["java"],
  Go: ["golang"],
  Rust: ["rust"],
  "C++": ["c++", "cpp"],
  "C#": ["c#", "csharp"],
  Ruby: ["ruby", "rails", "ruby on rails"],
  PHP: ["php"],
  Kotlin: ["kotlin"],
  Swift: ["swift"],
  SQL: ["sql"],
  PostgreSQL: ["postgresql", "postgres"],
  MySQL: ["mysql"],
  MongoDB: ["mongodb", "mongo"],
  Redis: ["redis"],
  Kafka: ["kafka"],
  GraphQL: ["graphql"],
  REST: ["restful", "rest api", "rest apis"],
  AWS: ["aws", "amazon web services"],
  Azure: ["azure"],
  GCP: ["gcp", "google cloud", "google cloud platform"],
  Kubernetes: ["kubernetes", "k8s"],
  Docker: ["docker"],
  Terraform: ["terraform"],
  Linux: ["linux", "unix"],
  Git: ["git", "github", "gitlab"],
  "CI/CD": ["ci/cd", "cicd", "continuous integration"],
  Spark: ["spark", "pyspark"],
  Airflow: ["airflow"],
  Snowflake: ["snowflake"],
  dbt: ["dbt"],
  Tableau: ["tableau"],
  "Power BI": ["power bi", "powerbi"],
  TensorFlow: ["tensorflow"],
  PyTorch: ["pytorch"],
  "Machine Learning": ["machine learning"],
  NLP: ["nlp", "natural language processing"],
  LLM: ["llm", "large language model", "generative ai", "genai"],
  Pega: ["pega", "pegasystems"],
  Salesforce: ["salesforce", "apex"],
  ServiceNow: ["servicenow"],
  SAP: ["sap", "abap"],
  Selenium: ["selenium"],
  Cypress: ["cypress"],
  Playwright: ["playwright"],
  Figma: ["figma"],
  HTML: ["html", "html5"],
  CSS: ["css", "css3", "scss", "sass"],
  Tailwind: ["tailwind", "tailwindcss"],
  Redux: ["redux"],
  Spring: ["spring", "spring boot"],
  Django: ["django"],
  Flask: ["flask"],
  ".NET": [".net", "dotnet", "asp.net"],
  FastAPI: ["fastapi"],
  Scala: ["scala"],
  DevOps: ["devops"],
  Microservices: ["microservices", "microservice"],
  gRPC: ["grpc"],
  Elasticsearch: ["elasticsearch", "elastic search"],
  Jenkins: ["jenkins"],
  Jira: ["jira"],
  Agile: ["agile", "scrum", "kanban"],

  /* Capabilities, not just stacks. Without these a posting like a defence
     robotics role reads as "two skills" and gets no score at all, which
     looks broken next to a scored card. */
  "Distributed Systems": ["distributed systems", "distributed system"],
  "System Design": ["system design", "systems design", "software architecture"],
  Algorithms: ["algorithms", "data structures"],
  Testing: ["unit testing", "unit tests", "test automation", "automated testing"],
  "API Design": ["api design", "apis", "api development"],
  Frontend: ["frontend", "front-end", "front end", "ui development"],
  Backend: ["backend", "back-end", "back end", "server-side"],
  "Full Stack": ["full stack", "full-stack", "fullstack"],
  iOS: ["ios"],
  Android: ["android"],
  "React Native": ["react native"],
  Embedded: ["embedded", "firmware", "rtos"],
  "Computer Vision": ["computer vision", "image processing"],
  Robotics: ["robotics", "autonomy", "motion planning"],
  Simulation: ["simulation", "modeling and simulation"],
  Networking: ["networking", "tcp/ip", "network protocols"],
  Security: ["cybersecurity", "application security", "infosec", "security engineering"],
  Cloud: ["cloud computing", "cloud-based", "cloud native"],
  ETL: ["etl", "elt", "data pipeline", "data pipelines"],
  "Data Modeling": ["data modeling", "data modelling", "dimensional modeling"],
  Analytics: ["analytics", "data analysis"],
  Observability: ["observability", "monitoring", "datadog", "grafana", "prometheus"],
};

/* Canonical names, longest alias first so "react.js" wins over "react". */
export const SKILL_NAMES = Object.keys(SKILL_ALIASES);

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/* Word-boundary match that survives the punctuation real postings use.
   Can't use \b — it breaks on the "+" and "#" inside C++ and C#, which is
   exactly where a naive matcher goes wrong. A full stop has to count as a
   boundary or every skill that ends a sentence ("...built in React.")
   silently fails to match. */
const hasTerm = (lowerText, term) =>
  new RegExp(`(^|[^a-z0-9+#])${escapeRe(term)}($|[^a-z0-9+#])`).test(lowerText);

/* The posting as one lowercase blob. */
export function jobText(job) {
  if (!job) return "";
  const h = job.job_highlights || {};
  const lists = ["Responsibilities", "Qualifications"]
    .flatMap((k) => (Array.isArray(h[k]) ? h[k] : []))
    .join("\n");
  const desc = typeof job.job_description === "string" ? job.job_description : "";
  return `${job.job_title || ""}\n${desc}\n${lists}`.toLowerCase();
}

/* Which canonical skills this posting actually asks for. */
export function skillsInText(lowerText) {
  return SKILL_NAMES.filter((name) =>
    SKILL_ALIASES[name].some((alias) => hasTerm(lowerText, alias))
  );
}

/* ---------- facts the posting states but our feed has no column for ----------
   Only ever read back what the text actually says; when it says nothing we
   return nothing rather than guessing a level from the title alone. */
const SENIORITY_RULES = [
  [/\b(intern|internship)\b/i, "Internship"],
  [/\b(principal|distinguished|fellow)\b/i, "Principal"],
  [/\b(staff|architect)\b/i, "Staff"],
  [/\b(director|head of|vp|vice president)\b/i, "Director+"],
  [/\b(manager|lead)\b/i, "Lead"],
  [/\b(sr\.?|senior)\b/i, "Senior"],
  [/\b(jr\.?|junior|entry[- ]level|associate|graduate|new grad)\b/i, "Entry"],
  [/\b(mid[- ]level|intermediate)\b/i, "Mid"],
];

export function jobFacts(job) {
  if (!job) return {};
  const title = job.job_title || "";
  const desc = typeof job.job_description === "string" ? job.job_description : "";

  const seniority = (SENIORITY_RULES.find(([re]) => re.test(title)) ||
    SENIORITY_RULES.find(([re]) => re.test(desc)) ||
    [])[1];

  /* "5+ years", "3-5 years", "minimum of 7 years" — take the smallest
     stated requirement, which is the bar you actually have to clear. */
  const years = [...desc.matchAll(/(\d{1,2})\s*\+?\s*(?:-\s*\d{1,2}\s*)?years?\b/gi)]
    .map((m) => Number(m[1]))
    .filter((n) => n > 0 && n <= 20);
  const yearsRequired = years.length ? Math.min(...years) : null;

  return { seniority, yearsRequired };
}

/* A posting that mentions only one or two recognisable skills gives a
   noisy ratio (one hit reads as 100%). Below this we show no score at
   all rather than a confident-looking wrong one. */
const MIN_ASKED = 3;

export function tierOf(pct) {
  return pct >= 75 ? "strong" : pct >= 50 ? "good" : "fair";
}

export const TIER_LABEL = { strong: "Strong match", good: "Good match", fair: "Fair match" };

/* Returns null when the posting doesn't give us enough to judge — callers
   should render nothing in that case, never a zero. */
export function scoreJob(job, userSkills) {
  const lower = jobText(job);
  if (!lower.trim()) return null;

  const asked = skillsInText(lower);
  if (asked.length < MIN_ASKED) return null;

  /* Resume skills are free text ("ReactJS", "Amazon Web Services") — map
     them onto the same canonical names so both sides compare like for like. */
  const mine = new Set();
  (userSkills || []).forEach((raw) => {
    const s = String(raw || "").toLowerCase().trim();
    if (!s) return;
    SKILL_NAMES.forEach((name) => {
      if (SKILL_ALIASES[name].some((alias) => alias === s || hasTerm(s, alias))) mine.add(name);
    });
  });

  const matched = asked.filter((s) => mine.has(s));
  const missing = asked.filter((s) => !mine.has(s));
  const pct = Math.round((matched.length / asked.length) * 100);

  return { pct, tier: tierOf(pct), matched, missing, asked: asked.length };
}
